import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Search } from "lucide-react";
import { PHONE_QUERY, useMediaQuery } from "@bastion/ui";
import { useOrgRendering } from "../useOrgRendering";
import { buildCommands, type CommandEntry } from "./commandPaletteCommands";
import styles from "./CommandPalette.module.css";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

export interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

function rankCommands(query: string, commands: CommandEntry[]): CommandEntry[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return commands;
  return commands
    .map((cmd) => {
      const labelLower = cmd.label.toLowerCase();
      const groupLower = cmd.group.toLowerCase();
      let score = 0;
      if (labelLower === trimmed) score += 100;
      else if (labelLower.startsWith(trimmed)) score += 60;
      else if (labelLower.includes(trimmed)) score += 40;
      else if (groupLower.includes(trimmed)) score += 20;
      else if (cmd.keywords.includes(trimmed)) score += 10;
      return { cmd, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.cmd);
}

export function CommandPalette({ isOpen, onClose, onNavigate }: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const isPhone = useMediaQuery(PHONE_QUERY);

  const me = useOrgRendering();
  const commands = useMemo(() => buildCommands(me), [me]);
  const filtered = useMemo(() => rankCommands(query, commands), [query, commands]);
  const grouped = useMemo(() => {
    const groups: Array<{ group: string; items: Array<{ cmd: CommandEntry; index: number }> }> = [];
    for (const [index, cmd] of filtered.entries()) {
      const last = groups[groups.length - 1];
      if (last?.group === cmd.group) {
        last.items.push({ cmd, index });
      } else {
        groups.push({ group: cmd.group, items: [{ cmd, index }] });
      }
    }
    return groups;
  }, [filtered]);

  // The input takes the focus when it mounts (autoFocus), in the same task
  // as the tap that opened the palette, so iOS raises the keyboard.
  useEffect(() => {
    if (!isOpen) return;
    setQuery("");
    setActiveIndex(0);
  }, [isOpen]);

  // iOS does not resize the page for the keyboard. On a phone the palette
  // takes the height of the visual viewport (--vv-height), so its last row
  // stays above the keyboard. Pinch zoom also changes the visual viewport,
  // so the value is removed while the page is zoomed.
  useEffect(() => {
    const overlay = overlayRef.current;
    const viewport = window.visualViewport;
    if (!isOpen || !isPhone || !overlay || !viewport) return;
    const update = () => {
      if (Math.abs(viewport.scale - 1) > 0.01) {
        overlay.style.removeProperty("--vv-height");
        return;
      }
      overlay.style.setProperty("--vv-height", `${viewport.height}px`);
    };
    update();
    viewport.addEventListener("resize", update);
    return () => {
      viewport.removeEventListener("resize", update);
      overlay.style.removeProperty("--vv-height");
    };
  }, [isOpen, isPhone]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Keep the active item in view as the user navigates with the keyboard.
  useEffect(() => {
    if (!isOpen) return;
    const node = listRef.current?.querySelector<HTMLLIElement>(
      `[data-active="true"]`,
    );
    node?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, isOpen, filtered.length]);

  const handleSelect = useCallback(
    (path: string) => {
      onNavigate(path);
      onClose();
    },
    [onNavigate, onClose],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (filtered.length === 0) return;
      setActiveIndex((i) => (i + 1) % filtered.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (filtered.length === 0) return;
      setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const selected = filtered[activeIndex];
      if (selected) handleSelect(selected.path);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div ref={overlayRef} className={styles.overlay} role="presentation" onClick={onClose}>
      <div
        className={styles.palette}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.searchRow}>
          <Search className={styles.searchIcon} aria-hidden="true" />
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Jump to a page or run a command…"
            spellCheck={false}
            autoComplete="off"
            className={styles.searchInput}
          />
        </div>
        <div className={styles.divider} />
        {filtered.length === 0 ? (
          <div className={styles.emptyState}>
            No matches for <span className={styles.emptyQuery}>{query}</span>
          </div>
        ) : (
          <ul ref={listRef} className={styles.list} role="listbox">
            {grouped.map((group) => (
              <li key={group.group} className={styles.group}>
                <div className={styles.groupLabel}>{group.group}</div>
                <ul className={styles.groupList}>
                  {group.items.map(({ cmd, index }) => {
                    const Icon = cmd.icon;
                    const active = index === activeIndex;
                    return (
                      <li
                        key={cmd.id}
                        data-active={active}
                        role="option"
                        aria-selected={active}
                        className={active ? styles.itemActive : styles.item}
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => handleSelect(cmd.path)}
                      >
                        <Icon className={styles.itemIcon} />
                        <span className={styles.itemBody}>
                          <span className={styles.itemLabel}>{cmd.label}</span>
                          <span className={styles.itemHint}>{cmd.hint}</span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
        <div className={styles.footer}>
          <span>
            <kbd>{isMac ? "⌘K" : "Ctrl K"}</kbd> search
          </span>
          <span>
            <kbd>{isMac ? "⌘⇧O" : "Ctrl ⇧ O"}</kbd> new chat
          </span>
          <span>
            <kbd>{isMac ? "⌘[" : "Ctrl ["}</kbd>
            <kbd>{isMac ? "⌘]" : "Ctrl ]"}</kbd> history
          </span>
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> navigate
          </span>
          <span>
            <kbd>↵</kbd> open
          </span>
          <span>
            <kbd>esc</kbd> close
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
